import type { GameView } from '@tanky/shared';
import { api } from '../api';
import { formatLimit } from '../clock';
import { h, toast } from '../dom';
import { go, session, type Screen } from '../session';
import { socket } from '../ws';
import { GameScreen } from '../game/GameScreen';
import { TrainingSession } from '../game/training';
import { tankThumb } from '../game/tankArt';
import { chatPanel } from './chatPanel';
import { tankEditor } from './tankEditor';
import { modeLabel } from './standings';
import { BOT_LEVELS, TANK_COLORS, WORLDS, type BotLevel } from '@tanky/shared';

export async function gameRoute(id: string): Promise<Screen | null> {
  let view: GameView;
  try {
    view = (await api.game(id)).game;
  } catch (e) {
    toast((e as Error).message, true);
    go('/');
    return null;
  }
  if (view.status === 'lobby') return roomScreen(view);
  return new GameScreen(view);
}

function reroute() {
  window.dispatchEvent(new HashChangeEvent('hashchange'));
}

function roomScreen(initial: GameView): Screen {
  let view = initial;
  let online = new Set<string>();
  const me = session.me!;
  const dyn = h('div', { class: 'stack' });
  const extras = h('div', { class: 'stack' });
  const body = h('div', { class: 'page' }, dyn, extras);
  const isMember = () => view.players.some((p) => p.id === me.id);
  const chat = chatPanel(view.id, { canWrite: isMember, allowBubble: () => false });
  chat.colorOf = (id) => {
    const i = view.players.findIndex((p) => p.id === id);
    return view.players[i]?.look?.color ?? TANK_COLORS[Math.max(0, i) % TANK_COLORS.length];
  };
  chat.setMessages(view.chat);
  const editor = tankEditor();
  const editorCard = h('details', { class: 'card' }, h('summary', {}, h('h3', { style: { display: 'inline' } }, '🛠 Můj tank')), editor.el);
  const chatCard = h('div', { class: 'card' }, h('h3', {}, '💬 Chat'), chat.el);

  const link = `${location.origin}/#/join/${view.code}`;

  async function share() {
    const text = `Pojď hrát Tanky! Kód hry: ${view.code}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Tanky', text, url: link });
        return;
      } catch {
        /* zrušeno – zkusíme schránku */
      }
    }
    try {
      await navigator.clipboard.writeText(link);
      toast('Odkaz zkopírován do schránky.');
    } catch {
      prompt('Zkopíruj odkaz:', link);
    }
  }

  let botLevel: BotLevel = 'normal';

  function botControls(): HTMLElement | null {
    if (view.ownerId !== me.id || view.players.length >= view.settings.maxPlayers) return null;
    const select = h(
      'select',
      { style: { width: 'auto', flex: '1' }, 'aria-label': 'Obtížnost bota' },
      ...BOT_LEVELS.map((l) => h('option', { value: l.id, selected: l.id === botLevel }, `${l.name}`)),
    ) as HTMLSelectElement;
    select.addEventListener('change', () => (botLevel = select.value as BotLevel));
    const btn = h(
      'button',
      {
        type: 'button',
        onclick: async () => {
          btn.disabled = true;
          try {
            view = { ...view, ...(await api.addBot(view.id, botLevel)).game };
            render();
          } catch (e) {
            toast((e as Error).message, true);
            btn.disabled = false;
          }
        },
      },
      '🤖 Přidat bota',
    );
    return h('div', { class: 'row bot-row' }, select, btn);
  }

  function render() {
    const isOwner = view.ownerId === me.id;
    const s = view.settings;
    const startBtn = h(
      'button',
      {
        class: 'primary',
        disabled: view.players.length < 2,
        onclick: async () => {
          startBtn.disabled = true;
          try {
            await api.start(view.id);
            reroute();
          } catch (e) {
            toast((e as Error).message, true);
            startBtn.disabled = false;
          }
        },
      },
      'Spustit hru',
    );
    dyn.replaceChildren(
      h('div', { class: 'topbar' }, h('button', { class: 'ghost', onclick: () => go('/') }, '← Lobby'), h('h2', {}, 'Čekárna')),
      h(
        'div',
        { class: 'card', style: { textAlign: 'center' } },
        h('div', { class: 'muted small' }, 'Kód hry'),
        h('div', { class: 'code' }, view.code),
        h('button', { onclick: share }, '📤 Sdílet odkaz'),
        h(
          'div',
          { class: 'muted small' },
          [modeLabel(view), WORLDS[s.style]?.name ?? 'Kopce', s.mode === 'deathmatch' ? null : `${formatLimit(s.turnLimitSec)} na tah`, `max. ${s.maxPlayers} hráčů`]
            .filter(Boolean)
            .join(' · '),
        ),
      ),
      h(
        'div',
        { class: 'card' },
        h('h3', {}, `Hráči (${view.players.length}/${s.maxPlayers})`),
        h(
          'ul',
          { class: 'player-list' },
          ...view.players.map((p, i) =>
            h(
              'li',
              {},
              p.bot ? h('span', { class: 'dot', style: { visibility: 'hidden' } }) : h('span', { class: `dot${online.has(p.id) ? ' on' : ''}` }),
              tankThumb(p.look, TANK_COLORS[i % TANK_COLORS.length], 44, p.id),
              h('span', { class: 'grow' }, p.name),
              p.id === view.ownerId ? h('span', { class: 'badge' }, 'zakladatel') : null,
              p.bot ? h('span', { class: 'badge' }, BOT_LEVELS.find((l) => l.id === p.bot)?.name ?? 'bot') : null,
              p.bot && isOwner
                ? h(
                    'button',
                    {
                      class: 'ghost icon-btn',
                      title: 'Odebrat bota',
                      'aria-label': 'Odebrat bota',
                      onclick: async () => {
                        try {
                          await api.removeBot(view.id, p.id);
                          refresh();
                        } catch (e) {
                          toast((e as Error).message, true);
                        }
                      },
                    },
                    '✕',
                  )
                : null,
            ),
          ),
        ),
        botControls(),
        !view.players.some((p) => p.id === me.id)
          ? h(
              'button',
              {
                class: 'primary',
                onclick: async () => {
                  try {
                    await api.join(view.code);
                    refresh();
                  } catch (e) {
                    toast((e as Error).message, true);
                  }
                },
              },
              'Připojit se',
            )
          : isOwner
            ? startBtn
            : h('p', { class: 'muted', style: { margin: '0' } }, 'Čeká se, až zakladatel spustí hru…'),
        h(
          'button',
          {
            class: 'ghost danger',
            onclick: async () => {
              if (!confirm(isOwner ? 'Opravdu zrušit hru?' : 'Opravdu opustit hru?')) return;
              try {
                await api.leave(view.id);
                go('/');
              } catch (e) {
                toast((e as Error).message, true);
              }
            },
          },
          isOwner ? 'Zrušit hru' : 'Opustit',
        ),
      ),
    );
    if (extras.childElementCount !== (isMember() ? 2 : 1)) {
      extras.replaceChildren(chatCard, ...(isMember() ? [editorCard] : []));
      chat.focus();
    }
  }

  async function refresh() {
    try {
      view = (await api.game(view.id)).game;
    } catch {
      toast('Hra byla zrušena.', true);
      go('/');
      return;
    }
    if (view.status !== 'lobby') reroute();
    else render();
  }

  const off = socket.on((m) => {
    if (m.t === 'game' && m.gameId === view.id) refresh();
    if (m.t === 'chat' && m.gameId === view.id) chat.add(m.msg);
    if (m.t === 'presence' && m.gameId === view.id) {
      online = new Set(m.online);
      render();
    }
  });
  const offOpen = socket.onOpen(refresh);
  socket.setGame(view.id);
  render();

  return {
    el: h('div', { class: 'backdrop' }, body),
    destroy() {
      off();
      offOpen();
      editor.destroy();
    },
  };
}

/** Tréninkový režim – lokální hra bez serveru. */
export function trainingScreen(): Screen {
  const me = session.me!;
  const training = new TrainingSession({ id: me.id, name: me.name, look: session.look });
  const state = training.state;
  const view: GameView = {
    id: 'training',
    code: '',
    status: 'playing',
    settings: { turnLimitSec: 0, maxPlayers: 1, style: state.style },
    ownerId: me.id,
    players: [{ id: me.id, name: me.name, look: session.look }],
    currentPlayerId: me.id,
    deadline: null,
    winnerId: null,
    turnSeq: 0,
    updatedAt: Date.now(),
    state,
    rematchId: null,
    serverNow: Date.now(),
    chat: [],
  };
  return new GameScreen(view, training);
}
